import React, { forwardRef, useEffect, useId, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import AccessibleProgressBar from '@/components/common/AccessibleProgressBar';
import '@/components/common/WizardStepIndicator.css';

// What each status looks like and what a screen reader hears for it
const STATUS_STYLES = {
    complete: {
        className: 'bg-success text-white',
        mark: '✓',
        srText: 'complete',
    },
    incomplete: {
        className: 'bg-warning-subtle text-dark border border-warning',
        mark: '!',
        srText: 'incomplete',
    },
    untouched: {
        className: 'bg-light text-muted',
        mark: null,
        srText: null,
    },
};

// Shared step-pill row used by both the faculty and student application
// wizards (CreateApplication.js, StudentApplicationPage.js).
//
// Without onSelect the pills are display only, coloured by position.
// With onSelect (faculty wizard, #463) they're buttons that jump to that
// section, coloured by `statuses` (complete, incomplete or untouched)
// instead, since "already passed" stops meaning "done" once people can
// skip around.
//
// The pill row only reads from lg up: below that, six or seven pills are
// too narrow for their labels. Below lg the wizard shows one "Step N of M"
// line and a progress bar instead, plus, when the pills jump between
// sections, a "Go to section" select that carries the same statuses.
//
// The ref marks the top of the step, which the wizards scroll to on a step
// change (useStepChangeFocus).

// The select only moves on Go: a select that navigates on change moves
// keyboard users who arrow through the options (WCAG 3.2.2)
function SectionJump({ total, current, labels, statuses, onSelect }) {
    const id = useId();
    const [target, setTarget] = useState(current);
    useEffect(() => setTarget(current), [current]);

    return (
        <div className="mt-3">
            <Form.Label htmlFor={id} className="small fw-semibold mb-1">
                Go to section
            </Form.Label>
            <InputGroup>
                <Form.Select
                    id={id}
                    value={target}
                    onChange={(e) => setTarget(Number(e.target.value))}
                >
                    {labels.slice(0, total).map((label, i) => {
                        const srText =
                            STATUS_STYLES[statuses?.[i]]?.srText ?? null;
                        return (
                            <option key={label} value={i + 1}>
                                {i + 1}. {label}
                                {srText ? ` (${srText})` : ''}
                            </option>
                        );
                    })}
                </Form.Select>
                <Button
                    variant="outline-primary"
                    onClick={() => onSelect(target)}
                >
                    Go
                </Button>
            </InputGroup>
        </div>
    );
}

function CompactStep({ total, current, labels, statuses, onSelect }) {
    return (
        <div className="d-lg-none">
            <p className="fw-semibold mb-2">
                Step {current} of {total}: {labels[current - 1]}
            </p>
            <AccessibleProgressBar
                now={current}
                max={total}
                label={`Step ${current} of ${total}`}
                className="wizard-step-progress"
            />
            {onSelect && (
                <SectionJump
                    total={total}
                    current={current}
                    labels={labels}
                    statuses={statuses}
                    onSelect={onSelect}
                />
            )}
        </div>
    );
}

const WizardStepIndicator = forwardRef(function WizardStepIndicator(
    { total, current, labels, statuses, onSelect },
    ref
) {
    const steps = Array.from({ length: total }, (_, i) => i + 1);

    if (!onSelect) {
        return (
            <div ref={ref} className="wizard-step-indicator mb-4">
                <CompactStep total={total} current={current} labels={labels} />
                <div className="d-none d-lg-flex justify-content-between">
                    {steps.map((s) => (
                        <div
                            key={s}
                            className={`wizard-step ${s < total ? 'me-2' : ''}`}
                            aria-current={s === current ? 'step' : undefined}
                        >
                            <div
                                className={`rounded-pill py-2 px-3 small h-100 d-flex align-items-center justify-content-center ${
                                    s === current
                                        ? 'bg-primary text-white'
                                        : s < current
                                          ? 'bg-success text-white'
                                          : 'bg-light text-muted'
                                }`}
                            >
                                {s}. {labels[s - 1]}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <nav
            ref={ref}
            aria-label="Application sections"
            className="wizard-step-indicator mb-4"
        >
            <CompactStep
                total={total}
                current={current}
                labels={labels}
                statuses={statuses}
                onSelect={onSelect}
            />
            {/* role="list" because list-unstyled makes Safari/VoiceOver
                stop announcing it as a list */}
            <ol
                role="list"
                className="list-unstyled d-none d-lg-flex justify-content-between mb-0"
            >
                {steps.map((s) => {
                    const status =
                        STATUS_STYLES[statuses?.[s - 1]] ??
                        STATUS_STYLES.untouched;
                    const isCurrent = s === current;
                    return (
                        <li
                            key={s}
                            className={`wizard-step ${s < total ? 'me-2' : ''}`}
                        >
                            {/* A bare .btn has no focus colour and turns
                                the outline off, so focus-ring puts a
                                visible keyboard focus back */}
                            <button
                                type="button"
                                className={`btn focus-ring w-100 h-100 d-flex align-items-center justify-content-center rounded-pill py-2 px-3 small ${
                                    isCurrent
                                        ? 'bg-primary text-white'
                                        : status.className
                                }`}
                                aria-current={isCurrent ? 'step' : undefined}
                                onClick={() => onSelect(s)}
                            >
                                {status.mark && (
                                    <span aria-hidden="true" className="me-1">
                                        {status.mark}
                                    </span>
                                )}
                                <span>
                                    {s}. {labels[s - 1]}
                                </span>
                                {status.srText && (
                                    <span className="visually-hidden">
                                        , {status.srText}
                                    </span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
});

export default WizardStepIndicator;
